import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { useFindAllActivities, useFindBusinessRelationships } from "~/gen/api"
import { validateLatitudeLongitudeTogether } from "~/pages/Admin/components/Fields/coordinatesSchema"
import { productionSiteFormSchema } from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import {
  isParentCompanyLinkResolved,
  type ParentCompanyLink,
} from "~/pages/SupplierPortal/ProductionSites/utils/productionSiteParentCompany.utils"
import { useGetOperations } from "~/utils/operations"

export type ProductionSiteFormSchemaProps = {
  readonly isEdit?: boolean
  readonly canEditParentCompany?: boolean
  readonly parentCompanyForEdit?: ParentCompanyLink
  readonly onDuplicateCandidateForCreateNew?: (
    duplicateCandidateId: string | null
  ) => void
}
export function useGetProductionSiteFormSchema(
  isEdit: boolean,
  onDuplicateCandidateForCreateNew?: (
    duplicateCandidateId: string | null
  ) => void,
  canEditParentCompany = false,
  parentCompanyForEdit?: ParentCompanyLink
) {
  const { t } = useTranslation("supplier")
  const { data: activities, isPending, isError } = useFindAllActivities()
  const {
    data: businessRelationshipOptions,
    isPending: isBusinessRelationshipsPending,
    isError: isBusinessRelationshipsError,
  } = useFindBusinessRelationships()
  const activitiesTrnaslations = useGetOperations({
    translationScope: "uniqueActivitiesLabels",
  })

  const activityOptions: ReadonlyArray<{
    readonly label: string
    readonly value: string
  }> = useMemo(() => {
    const list = activities?.data ?? []
    return list.map((a) => ({
      label: activitiesTrnaslations[a.name]?.name ?? a.name,
      value: a.id,
    }))
  }, [activities, activitiesTrnaslations])

  const registrationIdTypeOptions: ReadonlyArray<{
    readonly label: string
    readonly value: string
    readonly description?: string
  }> = useMemo(
    () => [
      {
        label: "VAT",
        value: "VAT",
        description: t("productionSiteModal.registrationIdDescriptions.VAT"),
      },
      {
        label: "EORI",
        value: "EORI",
        description: t("productionSiteModal.registrationIdDescriptions.EORI"),
      },
      {
        label: "GLN",
        value: "GLN",
        description: t("productionSiteModal.registrationIdDescriptions.GLN"),
      },
      {
        label: "DUNS",
        value: "DUNS",
        description: t("productionSiteModal.registrationIdDescriptions.DUNS"),
      },
      {
        label: "RCS",
        value: "RCS",
        description: t("productionSiteModal.registrationIdDescriptions.RCS"),
      },
      {
        label: "OTHER",
        value: "OTHER",
        description: t("productionSiteModal.registrationIdDescriptions.OTHER"),
      },
    ],
    [t]
  )

  const schema = useMemo(() => {
    const shape = productionSiteFormSchema.shape
    const parentCompanyOptions =
      businessRelationshipOptions?.data?.map((b) => ({
        label: b.name,
        value: b.supplierOrganizationId,
      })) ?? []
    const isParentCompanyEditableInEditMode = isEdit && canEditParentCompany
    const hasParentCompanyForEdit = parentCompanyForEdit !== undefined
    const isParentCompanyLinkResolvedForEdit =
      hasParentCompanyForEdit &&
      isParentCompanyLinkResolved(parentCompanyForEdit, parentCompanyOptions)
    const unresolvedParentCompanyName =
      hasParentCompanyForEdit &&
      isParentCompanyEditableInEditMode &&
      !isParentCompanyLinkResolvedForEdit
        ? parentCompanyForEdit.name.trim()
        : ""
    const parentCompanyPlaceholder =
      unresolvedParentCompanyName.length > 0
        ? unresolvedParentCompanyName
        : t("productionSiteModal.placeholders.parentCompany")
    const parentCompanyFieldSchema = isEdit
      ? z.string().trim()
      : shape.parentCompany

    return z
      .object({
        repositoryRecordId: shape.repositoryRecordId.meta({
          ...shape.repositoryRecordId.meta,
          fieldType: "hidden",
        }),
        name: shape.name.meta({
          ...shape.name.meta,
          label: t("productionSiteModal.labels.name"),
          tooltip: t("productionSiteModal.tooltips.name"),
          placeholder: t("productionSiteModal.placeholders.name"),
          fieldType: isEdit ? "text" : "productionSiteRepositoryAutocomplete",
          onDuplicateCandidateForCreateNew:
            isEdit === false ? onDuplicateCandidateForCreateNew : undefined,
        }),
        parentCompany: parentCompanyFieldSchema.meta({
          ...shape.parentCompany.meta,
          label: t("productionSiteModal.labels.parentCompany"),
          tooltip: isEdit
            ? canEditParentCompany
              ? t(
                  "productionSiteModal.tooltips.parentCompanyEdit",
                  "Select the company that owns this site."
                )
              : t(
                  "productionSiteModal.tooltips.parentCompanyReadOnly",
                  "Only the organization that created or owns this facility can change the parent company."
                )
            : t("productionSiteModal.tooltips.parentCompany"),
          fieldType: !isEdit
            ? "businessPartnerSelect"
            : isParentCompanyEditableInEditMode
              ? "businessPartnerSelect"
              : "text",
          disabled: isEdit && !canEditParentCompany,
          isRequired: !isEdit,
          placeholder: parentCompanyPlaceholder,
          creatable: !isEdit || isParentCompanyEditableInEditMode,
          options:
            isEdit && !canEditParentCompany ? undefined : parentCompanyOptions,
        }),
        registrationIds: shape.registrationIds.meta({
          ...shape.registrationIds.meta,
          label: t("productionSiteModal.labels.registrationId"),
          tooltip: t("productionSiteModal.tooltips.registrationId"),
          placeholder: t("productionSiteModal.placeholders.registrationId"),
          options: registrationIdTypeOptions,
        }),
        internalCode: shape.internalCode.meta({
          ...shape.internalCode.meta,
          label: t("productionSiteModal.labels.internalCode"),
          tooltip: t("productionSiteModal.tooltips.internalCode"),
          placeholder: t("productionSiteModal.placeholders.internalCode"),
        }),
        performedActivities: shape.performedActivities.meta({
          ...shape.performedActivities.meta,
          label: t("productionSiteModal.labels.performedActivities"),
          tooltip: t("productionSiteModal.tooltips.performedActivities"),
          options: activityOptions,
          placeholder: t(
            "productionSiteModal.placeholders.performedActivities"
          ),
        }),
        street: shape.street.meta({
          ...shape.street.meta,
          label: t("productionSiteModal.labels.street"),
          tooltip: t("productionSiteModal.tooltips.street"),
          placeholder: t("productionSiteModal.placeholders.street"),
          autoComplete: "address-line1",
        }),
        city: shape.city.meta({
          ...shape.city.meta,
          label: t("productionSiteModal.labels.city"),
          tooltip: t("productionSiteModal.tooltips.city"),
          placeholder: t("productionSiteModal.placeholders.city"),
          autoComplete: "address-level2",
        }),
        postalCode: shape.postalCode.meta({
          ...shape.postalCode.meta,
          label: t("productionSiteModal.labels.postalCode"),
          tooltip: t("productionSiteModal.tooltips.postalCode"),
          placeholder: t("productionSiteModal.placeholders.postalCode"),
          autoComplete: "postal-code",
        }),
        countryIso3166: shape.countryIso3166.meta({
          ...shape.countryIso3166.meta,
          label: t("productionSiteModal.labels.country"),
          tooltip: t("productionSiteModal.tooltips.country"),
          placeholder: t("productionSiteModal.placeholders.country"),
          autoComplete: "country",
        }),
        longitude: shape.longitude.meta({
          ...shape.longitude.meta,
          label: t("productionSiteModal.labels.longitude"),
          tooltip: t("productionSiteModal.tooltips.longitude"),
          placeholder: t("productionSiteModal.placeholders.longitude"),
        }),
        latitude: shape.latitude.meta({
          ...shape.latitude.meta,
          label: t("productionSiteModal.labels.latitude"),
          tooltip: t("productionSiteModal.tooltips.latitude"),
          placeholder: t("productionSiteModal.placeholders.latitude"),
        }),
      })
      .superRefine(validateLatitudeLongitudeTogether)
  }, [
    activityOptions,
    businessRelationshipOptions,
    canEditParentCompany,
    isEdit,
    onDuplicateCandidateForCreateNew,
    parentCompanyForEdit,
    registrationIdTypeOptions,
    t,
  ])

  return {
    schema,
    businessRelationshipOptions:
      businessRelationshipOptions?.data?.map((b) => ({
        label: b.name,
        value: b.supplierOrganizationId,
      })) ?? [],
    isLoading: isPending || isBusinessRelationshipsPending,
    isError: isError || isBusinessRelationshipsError,
  }
}
